// PlaybookChecklist — renders an AI-generated playbook (from
// `task-suggestions` mode=playbook) as a structured, step-by-step
// checklist inside the chat shell.
//
// What you get:
//   - Depth toggle (Short / Detailed / Expert) — refetches per depth.
//     Step count + body verbosity follow the depth contract.
//   - One step "current" at a time. Earlier steps collapse to a
//     checkmark + title; the current step auto-expands with body +
//     "Run in chat" button. Future steps stay collapsed but tappable.
//   - Per-step Run button seeds the chat composer with that step's
//     prompt and marks the step done.
//   - Progress bar + "Step X of Y" counter mirror the static playbook
//     strip's affordance, so the two surfaces feel like one system.
//
// This component is purely presentational over the hook — it owns
// completion state locally, scoped to the (kind, title) tuple via the
// `surface` key, and resets when the surface changes.
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, ChevronDown, ChevronRight, Loader2, RefreshCw, Sparkles, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePlaybookSteps } from "@/hooks/usePlaybookSteps";
import {
  DEPTH_META,
  PLAYBOOK_DEPTHS,
  usePlaybookDepth,
  type PlaybookDepth,
} from "@/lib/playbookDepth";
import type { TaskKind } from "@/hooks/useTaskSuggestions";

interface Props {
  /** Stable id for depth persistence + completion reset. */
  surface: string;
  kind: TaskKind;
  title: string;
  description?: string;
  context?: string;
  /** 1-5 difficulty (or label) → drives default depth. */
  difficulty?: number | string | null;
  /** Called when user taps Run on a step — host loads prompt into composer. */
  onRunStep: (step: { title: string; body: string; prompt: string; index: number }) => void;
  /** Hide entirely (e.g. surface collapsed). */
  enabled?: boolean;
  className?: string;
}

export default function PlaybookChecklist({
  surface,
  kind,
  title,
  description,
  context,
  difficulty,
  onRunStep,
  enabled = true,
  className,
}: Props) {
  const { depth, setDepth } = usePlaybookDepth({ surface, difficulty });
  const { steps, loading, error, regenerate } = usePlaybookSteps({
    kind,
    title,
    description,
    context,
    depth,
    enabled,
  });

  // Local completion state — reset when the surface or depth changes.
  const [done, setDone] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setDone(new Set());
    setExpanded(new Set());
  }, [surface, depth]);

  const total = steps.length;
  const doneCount = done.size;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  // Current = first not-done step. Auto-expand it so the user always
  // sees what to do next without an extra click.
  const currentIndex = useMemo(() => {
    for (let i = 0; i < total; i++) if (!done.has(i)) return i;
    return total > 0 ? total - 1 : 0;
  }, [done, total]);

  const isExpanded = (i: number) => expanded.has(i) || (i === currentIndex && !done.has(i));
  const toggleExpanded = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const markDone = (i: number) =>
    setDone((prev) => {
      if (prev.has(i)) return prev;
      const next = new Set(prev);
      next.add(i);
      return next;
    });

  const handleRun = (i: number) => {
    const s = steps[i];
    if (!s) return;
    onRunStep({ ...s, index: i });
    markDone(i);
  };

  if (!enabled) return null;

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-background/40 p-3 space-y-3",
        className,
      )}
    >
      {/* Header: title + depth toggle + progress */}
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          AI Playbook
        </span>
        <span className="text-[10px] font-mono text-muted-foreground/70 truncate max-w-[36ch]">
          · {title}
        </span>

        <div className="ml-auto flex items-center gap-1">
          {/* Depth toggle */}
          <div className="inline-flex items-center rounded-full border border-border bg-secondary/40 p-0.5">
            {PLAYBOOK_DEPTHS.map((d) => {
              const meta = DEPTH_META[d];
              const active = d === depth;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDepth(d)}
                  title={meta.hint}
                  className={cn(
                    "px-2 py-0.5 text-[10px] font-mono rounded-full transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={regenerate}
            disabled={loading}
            title="Regenerate playbook"
            className="inline-flex items-center justify-center rounded-full p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-40"
          >
            <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Progress row — only meaningful when we have steps */}
      {total > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
            Step{" "}
            <span className="text-primary font-semibold">
              {Math.min(currentIndex + 1, total)}
            </span>{" "}
            of <span className="text-foreground">{total}</span> · {doneCount} done
          </span>
          <div
            className="h-1 flex-1 rounded-full bg-secondary overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={doneCount}
          >
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && total === 0 && (
        <div className="space-y-1.5" aria-hidden>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded bg-secondary/50 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <p className="text-[11px] font-mono text-muted-foreground">
          Couldn't generate playbook ({error}). Try again in a moment.
        </p>
      )}

      {/* Empty */}
      {!loading && !error && total === 0 && (
        <p className="text-[11px] font-mono text-muted-foreground">
          No steps yet — pick a depth and the playbook will appear.
        </p>
      )}

      {/* Steps */}
      {total > 0 && (
        <ol className="space-y-1.5">
          {steps.map((s, i) => {
            const isDone = done.has(i);
            const isCurrent = i === currentIndex && !isDone;
            const open = isExpanded(i);
            return (
              <li
                key={`${i}-${s.title}`}
                className={cn(
                  "rounded-md border transition-colors",
                  isDone
                    ? "border-primary/30 bg-primary/5"
                    : isCurrent
                      ? "border-primary/60 bg-background ring-1 ring-primary/30"
                      : "border-border bg-background/60",
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleExpanded(i)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left"
                  aria-expanded={open}
                >
                  <span
                    className={cn(
                      "shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full border text-[10px] font-mono font-semibold",
                      isDone
                        ? "border-primary bg-primary text-primary-foreground"
                        : isCurrent
                          ? "border-primary text-primary"
                          : "border-border text-muted-foreground",
                    )}
                  >
                    {isDone ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  <span
                    className={cn(
                      "flex-1 min-w-0 text-[12px] font-mono truncate",
                      isDone ? "text-muted-foreground line-through" : "text-foreground",
                    )}
                    title={s.title}
                  >
                    {s.title}
                  </span>
                  {isCurrent && (
                    <span className="text-[9px] font-mono uppercase tracking-wider text-primary px-1.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 shrink-0">
                      Now
                    </span>
                  )}
                  {open ? (
                    <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                  )}
                </button>

                {open && (
                  <div className="px-2.5 pb-2.5 pl-9 space-y-2 border-t border-border/50 pt-2">
                    <div className="prose prose-xs prose-invert max-w-none text-[11px] font-mono text-muted-foreground leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {s.body}
                      </ReactMarkdown>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => handleRun(i)}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[10px] font-mono text-primary hover:bg-primary/20 hover:border-primary/60 transition-colors disabled:opacity-40"
                      >
                        {loading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        {isDone ? "Re-run in chat" : "Run in chat"}
                      </button>
                      {!isDone && (
                        <button
                          type="button"
                          onClick={() => markDone(i)}
                          className="text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Mark done
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}